import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "types/store";
import Select from "react-select";
import { ButtonGroup, Button } from "aible";
import { Overlay, Popover } from "react-bootstrap";
import Slider from "rc-slider";
import { isEnabled } from "utils/featureFlags";
import LabeledToggle from "components/Inputs/Toggle/Toggle";
import { LineInput } from "components/Inputs/LineInput";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import { DEFAULT_CACHE_AGE, MODEL_PARAMETERS } from "containers/ChatGPT/reducer";
import * as nextReducer from "containers/Next/NextReducer";
import ModelSelector from "components/ChatGPT/ModelSelector";
import ToneSelector from "components/ChatGPT/ToneSettings/ToneSelector";
import BlueprintSelector from "../BlueprintSelector";
import * as viewReducer from "containers/View/reducer";
import styles from "../style.module.css";
import ToneSettings from "components/ChatGPT/ToneSettings/ToneSettings";
import EditIcon from "images/edit-light.svg";
import GuardrailsSettings from "./GuardrailsSettings";
import { MultiSessionMemoryCreationToggle, MultiSessionMemoryToggle, MultiSessionMemoryTypeSettings } from "components/ChatGPT/ChatGPTSettings/ConversationMemorySettings";
import { getMultiSessionMemoryCreationEnabled, getMultiSessionMemoryEnabled } from "@/containers/ChatGPT/features/ConversationMemory/selectors";
const ConversationSettings = () => {
    const dispatch = useDispatch();
    const popupRef = useRef(null);
    // Redux
    const nextId = useSelector((state) => nextReducer.getCurrentNextId(state));
    const modelId = useSelector((state) => chatGPTReducer.getModelId(state));
    const chatType = useSelector((state) => chatGPTReducer.getChatType(state));
    const blueprintId = useSelector((state) => chatGPTReducer.getBlueprintId(state));
    const showCachePopup = useSelector((state) => chatGPTReducer.getShowCachePopup(state));
    const isNextSaving = useSelector((state) => nextReducer.getIsSavingReport(state, nextId));
    const isAsking = useSelector((state) => chatGPTReducer.getIsAsking(state));
    const disableButtons = isAsking || isNextSaving;
    const retrieverSearchType = useSelector((state) => chatGPTReducer.getRetrieverSearchType(state));
    const parentRetrieverSearchType = useSelector((state) => chatGPTReducer.getRetrieverSearchType(state, undefined, true));
    const retSearchType = retrieverSearchType ?? parentRetrieverSearchType;
    const isAnalytics = blueprintId === "CHATAIBLE_ANALYTICS" ||
        chatType === "AIBLE" ||
        chatType === "ANALYTICS" ||
        chatType === "WHATS_CHANGED";
    const isDocumentSummarization = blueprintId === "CHATAIBLE_DOCUMENT_SUMMARIZATION" || chatType === "DOCUMENT";
    const isNLQ = chatType === "SQL";
    const retrievalKSettingText = isAnalytics ? "charts for analysis" : "text excerpts for summarization";
    const updateRetrieverSearchType = (searchType) => dispatch(chatGPTReducer.updateRetrieverSearchType(searchType));
    const getSearchTypeLabel = () => {
        if (retSearchType === "bm25" || retSearchType === "bm25_only") {
            return "Exact Words";
        }
        if (retSearchType === "mmr" || retSearchType === "bm25_with_multi_query_with_mmr") {
            return "Exact Words & Concepts";
        }
        if (retSearchType === "retriever_mmr" || retSearchType === "multi_query_only_with_mmr") {
            return "Concepts";
        }
        return "Custom Search";
    };
    const stringToSpans = (string) => {
        return string.split(" ").map((word, index) => {
            return React.createElement("span", { key: index + "_" + word, className: styles.settingQuestionText }, `${word} `);
        });
    };
    const retrievalK = useSelector((state) => chatGPTReducer.getRetrievalK(state));
    const updateRetrievalK = (val) => dispatch(chatGPTReducer.updateRetrievalK(val));
    const parentRetrievalK = useSelector((state) => chatGPTReducer.getRetrievalK(state, undefined, true));
    const totalChartLimit = useSelector((state) => chatGPTReducer.getTotalChartLimit(state));
    const updateTotalChartLimit = (val) => dispatch(chatGPTReducer.updateTotalChartLimit(val));
    const parentTotalChartLimit = useSelector((state) => chatGPTReducer.getTotalChartLimit(state, undefined, true));
    const defaultChartLimit = MODEL_PARAMETERS?.[modelId]?.defaultChartLimit ?? MODEL_PARAMETERS?.["DEFAULTS"]?.defaultChartLimit;
    const maxChartLimit = MODEL_PARAMETERS?.[modelId]?.maxChartLimit ?? MODEL_PARAMETERS?.["DEFAULTS"]?.maxChartLimit;
    const maxRetrievalK = MODEL_PARAMETERS?.[modelId]?.maxRetrievalK ?? MODEL_PARAMETERS?.["DEFAULTS"]?.maxRetrievalK;
    const defaultRetrievalK = MODEL_PARAMETERS?.[modelId]?.defaultRetrievalK ?? MODEL_PARAMETERS?.["DEFAULTS"]?.defaultRetrievalK;
    const parentMark = React.createElement("div", { className: styles.lineageSliderMark });
    const systemPromptAugment = useSelector((state) => chatGPTReducer.getSystemPromptAugment(state, true, modelId, true));
    const updateSystemPromptAugment = (val) => dispatch(chatGPTReducer.updateSystemPromptAugment(val, modelId));
    const temperature = useSelector((state) => chatGPTReducer.getTemperature(state));
    const updateTemperature = (val) => dispatch(chatGPTReducer.updateTemperature(val));
    const temperatureLabel = temperature === 0 ? "Repeatable" : temperature === 1 ? "Creative" : "Somewhat Creative";
    const useSystemPromptAugment = useSelector((state) => chatGPTReducer.getUseSystemPromptAugment(state));
    const showTokens = useSelector((state) => chatGPTReducer.getShowTokens(state));
    const updateShowTokens = (val) => dispatch(chatGPTReducer.updateShowTokens(val));
    const tokenCostSettingText = "Maximum cost allowed for each answer is";
    const isAskingGenerateStyles = useSelector((state) => chatGPTReducer.getIsAskingGeneratedStyles(state));
    // Cache-related settings
    const forceCacheSave = useSelector(chatGPTReducer.getForceCacheSave);
    // Cache-related update functions
    const updateShowCachePopup = (val) => dispatch(chatGPTReducer.updateShowCachePopup(val));
    const updateMaxCacheAge = (maxCacheAge) => dispatch(chatGPTReducer.updateMaxCacheAge(maxCacheAge));
    const updateForceCacheSave = (forceCacheSave) => dispatch(chatGPTReducer.updateForceCacheSave(forceCacheSave));
    const updateCacheSimilarityScoreCutoff = (cacheSimilarityScoreCutoff) => dispatch(chatGPTReducer.updateCacheSimilarityScoreCutoff(cacheSimilarityScoreCutoff));
    const chatHistoryCount = useSelector((state) => chatGPTReducer.getChatHistoryCount(state));
    // NLQ settings
    const ignoreNlqDataFromRedux = useSelector((state) => chatGPTReducer.getIgnoreNlqData(state));
    const [ignoreNlqData, updateIgnoreNlqData] = useState(ignoreNlqDataFromRedux || false);
    useEffect(() => {
        dispatch(chatGPTReducer.updateIgnoreNlqData(ignoreNlqData));
    }, [ignoreNlqData, dispatch]);
    const [promptStyling, updatePromptStyling] = useState("");
    const [showToneSettings, setShowToneSettings] = useState(false);
    const smallerButtonStyle = { padding: "5px" };
    const sliderHandleStyle = {
        backgroundColor: "#9F85F2",
        borderColor: "white",
        boxShadow: "0px 0px 6px 1px rgba(81, 47, 119, 0.35)",
        zIndex: 100,
        pointer: "cursor !important"
    };
    const chatHistorySettingText = chatHistoryCount === 1 ? "previous question for context" : "previous questions for context";
    const chatHistoryLabel = chatHistoryCount == 0 ? "Do Not Recall" : `Recall ${chatHistoryCount}`;
    const maxCacheAge = useSelector((state) => chatGPTReducer.getMaxCacheAge(state));
    const cacheAgeLabel = maxCacheAge === 1000 * 60 * 60
        ? "1 Hour"
        : maxCacheAge === 1000 * 60 * 60 * 24
            ? "1 Day"
            : maxCacheAge === DEFAULT_CACHE_AGE
                ? "1 Week"
                : "Custom";
    const parentHistoryCount = useSelector((state) => chatGPTReducer.getChatHistoryCount(state, undefined, true));
    const cacheSimilarityScoreCutoff = useSelector((state) => chatGPTReducer.getCacheSimilarityScoreCutoff(state));
    const cacheSettingText = "Generate a new response if the cached version is older than";
    const cacheSearchSettingText = "See cached responses that match the question's";
    const cacheSimilarityLabel = cacheSimilarityScoreCutoff === undefined
        ? "Exact Words"
        : cacheSimilarityScoreCutoff <= 0.8
            ? "Approximate Idea"
            : cacheSimilarityScoreCutoff < 1
                ? "Intent"
                : "Custom";
    const showChatHistoryWarning = (chatType === "SQL" || chatType === "DOCUMENT" || chatType === "AIBLE" || chatType === "WHATS_CHANGED") &&
        chatHistoryCount > 0 &&
        isEnabled("completion-cache") &&
        maxCacheAge !== undefined;
    const chatClearInput = useSelector((state) => chatGPTReducer.getChatClearInput(state));
    const updateChatClearInput = (val) => dispatch(chatGPTReducer.updateChatClearInput(val));
    const agentIQTools = useSelector((state) => chatGPTReducer.getAgentIQTools(state));
    const agentIQToolsOptions = useSelector((state) => chatGPTReducer.getAgentIQToolsOptions(state));
    const chatRestrictChartsToPinned = useSelector((state) => chatGPTReducer.getChatRestrictChartsToPinned(state));
    const updateChatRestrictChartsToPinned = (val) => dispatch(chatGPTReducer.updateChatRestrictChartsToPinned(val));
    const agentAutonomyView = useSelector((state) => viewReducer.getAgentAutonomyView(state));
    const showGuardrailsSetting = isEnabled("chat-guardrails") && blueprintId !== "AGENT_IQ" && agentAutonomyView !== "HIGH";
    const multiSessionMemoryEnabled = useSelector(getMultiSessionMemoryEnabled);
    const multiSessionMemoryCreationEnabled = useSelector(getMultiSessionMemoryCreationEnabled);
    return (React.createElement("div", { className: styles.simpleOuter, style: { maxHeight: "100%", overflowX: "hidden" } },
        React.createElement("div", { className: styles.simpleChatBlock },
            React.createElement("div", { className: styles.simpleHeader }, isEnabled("chat-aible-model-ranking-selector")
                ? "AI Model: What is most important for your answers?"
                : "AI MODEL"),
            React.createElement("div", { style: {
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "20px"
                } },
                isEnabled("chat-aible-model-ranking-selector") && (React.createElement(ButtonGroup, { style: { flexShrink: 0 } },
                    React.createElement(Button, { style: smallerButtonStyle, onClick: () => dispatch(chatGPTReducer.updateModelId("gemini-1.5-pro")), active: modelId === "gemini-1.5-pro", greyscaleDefaultState: true }, "Best Potential Answer"),
                    React.createElement(Button, { style: smallerButtonStyle, onClick: () => dispatch(chatGPTReducer.updateModelId("093f2646-1e0e-4d70-9465-1f950d435377")), active: modelId === "093f2646-1e0e-4d70-9465-1f950d435377", greyscaleDefaultState: true }, "Cheapest"),
                    React.createElement(Button, { style: smallerButtonStyle, onClick: () => dispatch(chatGPTReducer.updateModelId("mistral-7b")), active: modelId === "mistral-7b", greyscaleDefaultState: true }, "Fastest"))),
                React.createElement("div", { style: {
                        display: "flex",
                        alignItems: "center"
                    } },
                    React.createElement("span", { style: { color: "#202939" } }, "Selected Model"),
                    React.createElement(ModelSelector, { hideIcons: true, isCreatable: true, selectorStyle: {
                            marginLeft: "15px"
                        }, style: { flexShrink: "1", whiteSpace: "normal" } }))),
            showGuardrailsSetting && React.createElement(GuardrailsSettings, { disableButtons: disableButtons }),
            isEnabled("chat-conversation-memory") && (React.createElement("div", { className: "mt-[14px]" },
                React.createElement(MultiSessionMemoryCreationToggle, { disableButtons: disableButtons }),
                multiSessionMemoryCreationEnabled && (React.createElement(MultiSessionMemoryTypeSettings, { setting: "enableCreation", hidePrompts: true, disableButtons: disableButtons, allowCreation: true, simpleView: true })),
                React.createElement(MultiSessionMemoryToggle, { disableButtons: disableButtons }),
                multiSessionMemoryEnabled && (React.createElement(MultiSessionMemoryTypeSettings, { setting: "enableAugmentation", simpleView: true, disableButtons: disableButtons }))))),
        isEnabled("generated-styles") && (React.createElement("div", { className: styles.simpleChatBlock, style: { color: "black" } },
            React.createElement("div", { className: styles.simpleHeader }, "Generate CSS styling from LLM"),
            React.createElement("div", { style: { marginBottom: "4px" } },
                React.createElement("span", null, "Enter prompt for LLM to use for styling purposes")),
            React.createElement(LineInput, { value: promptStyling, onChange: (val) => updatePromptStyling(val) }),
            React.createElement(Button, { style: { marginTop: "10px" }, onClick: () => dispatch(chatGPTReducer.askGenerateStyles(promptStyling)), isLoading: isAskingGenerateStyles }, "Generate CSS"),
            React.createElement(Button, { style: { marginTop: "10px", marginLeft: "10px" }, onClick: () => dispatch(chatGPTReducer.askGenerateStylesExport(promptStyling)), isLoading: isAskingGenerateStyles }, "Generate Export"))),
        isNLQ && isEnabled("allow-ignore-nlq-data") && (React.createElement("div", { className: styles.simpleChatBlock, style: { color: "black" } },
            React.createElement("div", { className: styles.simpleHeader }, "Natural Language Query Settings"),
            React.createElement("div", { className: styles.settingFlex },
                React.createElement("div", { className: styles.settingLabel }, "Do not include NLQ Data"),
                React.createElement(LabeledToggle, { style: { display: "block" }, checked: ignoreNlqData, onChange: () => {
                        updateIgnoreNlqData(!ignoreNlqData);
                    }, disabled: disableButtons, isAibleColors: true })))),
        React.createElement("div", { className: styles.simpleChatBlock },
            React.createElement("div", { className: styles.simpleHeader }, "Guidelines for how the AI responds to questions"),
            React.createElement("div", { style: { marginBottom: "4px" } },
                React.createElement("span", null, "I want the answers to be"),
                React.createElement("span", { style: { marginLeft: "3px", fontWeight: "bold" } }, temperatureLabel)),
            React.createElement(ButtonGroup, null,
                React.createElement(Button, { style: smallerButtonStyle, onClick: () => {
                        updateTemperature(0);
                    }, active: temperature === 0, greyscaleDefaultState: true }, "Repeatable"),
                React.createElement(Button, { style: smallerButtonStyle, onClick: () => {
                        updateTemperature(0.5);
                    }, active: temperature == 0.5, greyscaleDefaultState: true }, "Somewhat Creative"),
                React.createElement(Button, { style: smallerButtonStyle, onClick: () => {
                        updateTemperature(1);
                    }, active: temperature === 1, greyscaleDefaultState: true }, "Creative")),
            React.createElement("div", { style: {
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                } },
                React.createElement("div", { style: { width: "100px" } }, "Agent Template"),
                React.createElement("div", { style: { width: "311px" } },
                    React.createElement(BlueprintSelector, { isDisabled: isEnabled("spark-with-partners") }))),
            blueprintId === "AGENT_IQ" && (React.createElement("div", { style: {
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                } },
                React.createElement("div", { style: { width: "100px" } }, "Tools"),
                React.createElement("div", { style: { width: "311px" } },
                    React.createElement(Select, { isMulti: true, options: agentIQToolsOptions, onChange: (val) => dispatch(chatGPTReducer.updateAgentIQTools(val)), value: agentIQTools })))),
            (isDocumentSummarization || isAnalytics) && (React.createElement(React.Fragment, null,
                React.createElement("div", { style: {
                        marginTop: "20px",
                        display: "flex",
                        justifyContent: "space-between"
                    } }, isDocumentSummarization && (React.createElement(React.Fragment, null,
                    React.createElement("div", { style: { marginBottom: "4px" } },
                        React.createElement("span", null, "Find content that matches my questions"),
                        React.createElement("span", { style: { marginLeft: "3px", fontWeight: "bold" } }, getSearchTypeLabel())),
                    React.createElement(ButtonGroup, { style: {
                            marginBottom: "20px",
                            display: "flex",
                            justifyContent: "space-between"
                        } },
                        React.createElement(Button, { style: smallerButtonStyle, onClick: () => {
                                updateRetrieverSearchType("bm25");
                            }, active: retSearchType === "bm25" || retSearchType === "bm25_only" }, "Exact Words"),
                        React.createElement(Button, { style: smallerButtonStyle, onClick: () => {
                                updateRetrieverSearchType("mmr");
                            }, active: retSearchType === "mmr" || retSearchType === "bm25_with_multi_query_with_mmr" }, "Both"),
                        React.createElement(Button, { style: smallerButtonStyle, onClick: () => {
                                updateRetrieverSearchType("retriever_mmr");
                            }, active: retSearchType === "retriever_mmr" || retSearchType === "multi_query_only_with_mmr" }, "Concepts"))))),
                React.createElement("div", { style: {
                        marginTop: "20px",
                        display: "flex",
                        justifyContent: "space-between"
                    } },
                    React.createElement("div", { style: { width: "100%" } },
                        React.createElement("div", { className: styles.settingFlex },
                            React.createElement("div", { className: styles.settingLabelAlignedEnd },
                                React.createElement("span", { className: styles.settingQuestionText }, "Send up to"),
                                React.createElement("span", { className: styles.settingValueText }, isAnalytics
                                    ? totalChartLimit
                                    : retrievalK <= maxRetrievalK
                                        ? retrievalK
                                        : `${retrievalK} (CUSTOM)`),
                                stringToSpans(retrievalKSettingText)),
                            isDocumentSummarization && (React.createElement("div", null,
                                React.createElement(LineInput, { value: retrievalK, onChange: (val) => updateRetrievalK(Number(val)) })))),
                        React.createElement("div", { className: styles.simpleSliderHolder }, isAnalytics ? (React.createElement(Slider, { marks: {
                                [parentTotalChartLimit]: parentMark,
                                1: "1",
                                [defaultChartLimit]: `${defaultChartLimit} (Default)`,
                                [maxChartLimit]: maxChartLimit
                            }, min: 1, max: maxChartLimit, included: false, trackStyle: { backgroundColor: "transparent" }, handleStyle: sliderHandleStyle, onChange: (val) => updateTotalChartLimit(val), value: totalChartLimit })) : (React.createElement(Slider, { marks: {
                                [parentRetrievalK]: parentMark,
                                1: "1",
                                [defaultRetrievalK]: defaultRetrievalK,
                                [maxRetrievalK]: maxRetrievalK
                            }, min: 1, max: maxRetrievalK, included: false, trackStyle: { backgroundColor: "transparent" }, handleStyle: sliderHandleStyle, onChange: (val) => updateRetrievalK(val), value: retrievalK }))))))),
            useSystemPromptAugment && (React.createElement("div", { style: { marginTop: "20px" } },
                React.createElement("textarea", { className: styles.settingPrompt, value: systemPromptAugment, onChange: (val) => updateSystemPromptAugment(val.target.value), disabled: disableButtons }))),
            React.createElement("div", { style: {
                    marginTop: "20px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                } },
                React.createElement("div", { style: { width: "100px" } }, "Tone"),
                React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "5px" } },
                    React.createElement("div", { style: { width: "250px" } },
                        React.createElement(ToneSelector, null)),
                    React.createElement("div", null,
                        React.createElement(Button, { type: "secondary", onClick: () => setShowToneSettings(!showToneSettings) },
                            React.createElement("img", { src: EditIcon, style: { width: "18px" } }))))),
            showToneSettings && (React.createElement("div", { style: { marginTop: "20px" } },
                React.createElement(ToneSettings, null)))),
        !isEnabled("chat-aible-hide-token-costs") && (React.createElement("div", { className: styles.simpleChatBlock },
            React.createElement("div", { className: styles.simpleHeader }, "Cost and Speed Options"),
            isEnabled("completion-cache") && (React.createElement("div", { className: styles.settingFlex + " " + (showCachePopup ? styles.active : "") },
                React.createElement("div", { className: styles.settingLabel, style: { position: "relative" }, ref: popupRef },
                    showCachePopup && (React.createElement(Overlay, { show: showCachePopup, onHide: () => updateShowCachePopup(false), placement: "top", container: popupRef.current, rootClose: true },
                        React.createElement(Popover, { id: "cachePopup", placement: "top", 
                            // positionTop={-80}
                            style: { marginTop: "-100px" }, onClick: () => {
                                updateShowCachePopup(false);
                            } },
                            React.createElement("div", { className: styles.cachePopup },
                                React.createElement("div", { className: styles.cacheToggleTooltipText }, maxCacheAge === undefined
                                    ? "You have caching disabled. Enable caching to modify the age limit and matching strictness. "
                                    : "You can modify the caching age limit, matching strictness, or turn it off here."))))),
                    React.createElement("span", null, "Reduce costs by showing cached responses to matching questions")),
                React.createElement(LabeledToggle, { style: { display: "block" }, checked: maxCacheAge !== undefined, onChange: () => {
                        updateShowCachePopup(false);
                        updateMaxCacheAge(maxCacheAge === undefined ? DEFAULT_CACHE_AGE : undefined);
                    }, disabled: disableButtons, isAibleColors: true }))),
            isEnabled("completion-cache") && maxCacheAge !== undefined && (React.createElement(React.Fragment, null,
                React.createElement("div", null,
                    React.createElement("div", { className: styles.settingFlex },
                        React.createElement("div", { className: styles.settingLabelAlignedEnd },
                            stringToSpans(cacheSettingText),
                            React.createElement("span", { className: styles.settingValueText }, `${cacheAgeLabel}`))),
                    React.createElement("div", { className: styles.sliderHolder },
                        React.createElement(Slider, { marks: {
                                0: "1 HOUR",
                                1: "1 DAY",
                                2: "1 WEEK"
                            }, min: 0, max: 2, value: maxCacheAge === 1000 * 60 * 60 ? 0 : maxCacheAge === 1000 * 60 * 60 * 24 ? 1 : 2, trackStyle: { backgroundColor: "transparent" }, handleStyle: sliderHandleStyle, onChange: (val) => updateMaxCacheAge(val < 1 ? 1000 * 60 * 60 : val < 2 ? 1000 * 60 * 60 * 24 : DEFAULT_CACHE_AGE) }))),
                React.createElement("div", null,
                    React.createElement("div", { className: styles.settingFlex },
                        React.createElement("div", { className: styles.settingLabelAlignedEnd },
                            stringToSpans(cacheSearchSettingText),
                            React.createElement("span", { className: styles.settingValueText }, cacheSimilarityLabel))),
                    React.createElement("div", { className: styles.sliderHolder },
                        React.createElement(Slider, { marks: {
                                0: "EXACT WORDS",
                                1: "INTENT",
                                2: "APPROXIMATE IDEA"
                            }, min: 0, max: 2, value: cacheSimilarityScoreCutoff === undefined
                                ? 0
                                : cacheSimilarityScoreCutoff <= 0.8
                                    ? 2
                                    : cacheSimilarityScoreCutoff < 1
                                        ? 1
                                        : 0, trackStyle: { backgroundColor: "transparent" }, handleStyle: sliderHandleStyle, onChange: (val) => updateCacheSimilarityScoreCutoff(val < 1 ? undefined : val < 2 ? 0.95 : 0.8) }))),
                React.createElement("div", { className: styles.settingFlex + " " + (showCachePopup ? styles.active : "") },
                    React.createElement("div", { className: styles.settingLabel, style: { position: "relative" }, ref: popupRef },
                        React.createElement("span", null, "Always cache responses even in the case of a failure")),
                    React.createElement(LabeledToggle, { style: { display: "block" }, checked: forceCacheSave === true, onChange: () => {
                            updateShowCachePopup(false);
                            updateForceCacheSave(!forceCacheSave);
                        }, disabled: disableButtons, isAibleColors: true })))))),
        React.createElement("div", { className: styles.simpleChatBlock },
            React.createElement("div", { className: styles.simpleHeader }, "Other Options"),
            isAnalytics && (React.createElement("div", { style: {
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px",
                    marginBottom: "10px"
                } },
                React.createElement("div", { className: styles.settingLabel }, "Restrict chat to pinned explore charts"),
                React.createElement(LabeledToggle, { style: { display: "block" }, checked: chatRestrictChartsToPinned, onChange: () => {
                        return updateChatRestrictChartsToPinned(!chatRestrictChartsToPinned);
                    }, disabled: disableButtons, isAibleColors: true }))),
            React.createElement("div", { style: {
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "10px"
                } },
                React.createElement("div", { className: styles.settingLabel }, "Clear input upon asking questions"),
                React.createElement(LabeledToggle, { style: { display: "block" }, checked: chatClearInput, onChange: () => {
                        if (chatClearInput) {
                            return updateChatClearInput(false);
                        }
                        return updateChatClearInput(true);
                    }, disabled: disableButtons, isAibleColors: true })))));
};
export default ConversationSettings;
